import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { statsService, departamentoService, okrService, usuarioService, empresaService, keyResultService } from '@/services/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Building2, Users, Target, TrendingUp, CheckCircle2, Clock, AlertCircle, XCircle, PauseCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { maskCNPJ } from '@/lib/masks';
import { OKRsByStatusChart, OKRsByDepartmentChart, UsersByTypeChart, ProgressByDepartmentChart, UsersByDepartmentChart, OKRsByCompanyChart, UsersByCompanyChart, CompletionRateByDepartmentChart, KRProgressChart, CompanyProgressRankingChart, CompletionRateByCompanyChart } from '@/components/dashboard/DashboardCharts';
import { OKR, Departamento, Usuario, Empresa, KeyResult } from '@/types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  className?: string;
  href?: string;
}

function StatCard({
  title,
  value,
  icon,
  description,
  className,
  href
}: StatCardProps) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (href) {
      navigate(href);
    }
  };

  return (
    <Card 
      className={cn(
        "card-interactive",
        href && "cursor-pointer hover:border-primary/50 transition-colors",
        className
      )}
      onClick={handleClick}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}
function StatusCard({
  status,
  count,
  total
}: {
  status: string;
  count: number;
  total: number;
}) {
  const config = {
    a_fazer: {
      label: 'A Fazer',
      icon: AlertCircle,
      className: 'status-todo'
    },
    fazendo: {
      label: 'Fazendo',
      icon: Clock,
      className: 'status-doing'
    },
    feito: {
      label: 'Feito',
      icon: CheckCircle2,
      className: 'status-done'
    },
    pausado: {
      label: 'Pausado',
      icon: PauseCircle,
      className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    },
    cancelado: {
      label: 'Cancelado',
      icon: XCircle,
      className: 'bg-red-100 text-red-800'
    }
  }[status] || {
    label: status,
    icon: Target,
    className: ''
  };
  const percentage = total > 0 ? Math.round(count / total * 100) : 0;
  return <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-border">
      <div className="flex items-center gap-3">
        <div className={cn("status-badge", config.className)}>
          <config.icon className="h-3.5 w-3.5 mr-1" />
          {config.label}
        </div>
        <span className="text-2xl font-bold">{count}</span>
      </div>
      <div className="text-sm text-muted-foreground">{percentage}%</div>
    </div>;
}
export default function Dashboard() {
  const {
    user,
    empresaAtiva,
    empresaFiltroId,
  } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [empresaCnpj, setEmpresaCnpj] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        let response;
        if (user?.tipo === 'superadmin') {
          // Com uma empresa específica selecionada no seletor global do header, mostra as
          // estatísticas dela (mesmo endpoint usado por admin); com "Todas as empresas"
          // (empresaFiltroId nulo), mostra a visão consolidada de sempre.
          response = empresaFiltroId
            ? await statsService.getAdminStats(empresaFiltroId)
            : await statsService.getSuperAdminStats();
          // Load additional data for charts
          const [depsRes, okrsRes, usersRes, empresasRes, krsRes] = await Promise.all([departamentoService.getAll(), okrService.getAll(), usuarioService.getAll(), empresaService.getAll(), keyResultService.getAll()]);
          if (depsRes.success) setDepartamentos(depsRes.data);
          if (okrsRes.success) setOkrs(okrsRes.data);
          if (usersRes.success) setUsuarios(usersRes.data);
          if (empresasRes.success) setEmpresas(empresasRes.data);
          if (krsRes.success) setKeyResults(krsRes.data);
        } else if (user?.tipo === 'admin' && empresaAtiva?.id) {
          response = await statsService.getAdminStats(empresaAtiva.id);
          // Load additional data for charts
          const [depsRes, okrsRes, usersRes, krsRes, empresaRes] = await Promise.all([departamentoService.getByEmpresa(empresaAtiva.id), okrService.getByEmpresa(empresaAtiva.id), usuarioService.getByEmpresa(empresaAtiva.id), keyResultService.getAll(), empresaService.getById(empresaAtiva.id)]);
          if (depsRes.success) setDepartamentos(depsRes.data);
          if (okrsRes.success) setOkrs(okrsRes.data);
          if (usersRes.success) setUsuarios(usersRes.data);
          if (krsRes.success) setKeyResults(krsRes.data);
          setEmpresaCnpj(empresaRes.success ? empresaRes.data.cnpj || null : null);
        } else if (empresaAtiva?.id) {
          response = await statsService.getUserStats(empresaAtiva.id);
          // Load OKRs and KRs for charts
          const [okrsRes, depsRes, krsRes, empresaRes] = await Promise.all([okrService.getByEmpresa(empresaAtiva.id), departamentoService.getByEmpresa(empresaAtiva.id), keyResultService.getAll(), empresaService.getById(empresaAtiva.id)]);
          if (okrsRes.success) setOkrs(okrsRes.data);
          if (depsRes.success) setDepartamentos(depsRes.data);
          if (krsRes.success) setKeyResults(krsRes.data);
          setEmpresaCnpj(empresaRes.success ? empresaRes.data.cnpj || null : null);
        } else {
          setEmpresaCnpj(null);
        }
        if (response?.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadStats();
  }, [user, empresaAtiva, empresaFiltroId]);

  // Prepare chart data
  const departmentChartData = departamentos.map(dep => ({
    id: dep.id,
    nome: dep.nome,
    okrCount: okrs.filter(o => o.departamento_id === dep.id).length
  }));
  const usersByTypeData = Object.entries(usuarios.reduce((acc, u) => {
    acc[u.tipo] = (acc[u.tipo] || 0) + 1;
    return acc;
  }, {} as Record<string, number>)).map(([tipo, count]) => ({
    tipo,
    count
  }));
  const progressByDepartmentData = departamentos.map(dep => {
    const depOkrs = okrs.filter(o => o.departamento_id === dep.id);
    const avgProgress = depOkrs.length > 0 ? Math.round(depOkrs.reduce((sum, o) => sum + o.progresso_geral, 0) / depOkrs.length) : 0;
    return {
      name: dep.nome,
      value: avgProgress
    };
  });

  // New chart data: Completion rate by department
  const completionRateByDepartmentData = departamentos.map(dep => {
    const depOkrs = okrs.filter(o => o.departamento_id === dep.id);
    const completedCount = depOkrs.filter(o => o.status === 'feito').length;
    const rate = depOkrs.length > 0 ? Math.round(completedCount / depOkrs.length * 100) : 0;
    return {
      name: dep.nome,
      value: rate
    };
  });

  // OKRs by company (for superadmin)
  const okrsByCompanyData = empresas.map(emp => ({
    empresa: emp.nome,
    count: okrs.filter(o => o.empresa_id === emp.id).length
  }));

  // Users by company (for superadmin)
  const usersByCompanyData = empresas.map(emp => ({
    empresa: emp.nome,
    count: usuarios.filter(u => u.empresa_id === emp.id).length
  }));

  // Key Results progress data (top 6)
  const krProgressData = keyResults.filter(kr => okrs.some(o => o.id === kr.okr_id && (user?.tipo === 'superadmin' || o.empresa_id === user?.empresa_id))).slice(0, 6).map(kr => ({
    name: kr.descricao.length > 20 ? kr.descricao.substring(0, 20) + '...' : kr.descricao,
    value: kr.percentual
  }));

  const isSuperAdmin = user?.tipo === 'superadmin';
  // Empresa "selecionada" pro drill-down do superadmin vem do seletor global do header
  // (empresaFiltroId) — "Todas as empresas" (null) cai na visão comparativa de sempre.
  const empresaSelecionada = isSuperAdmin ? (empresaFiltroId ?? 'all') : 'all';

  // Dados comparativos entre empresas (superadmin, visão consolidada)
  const companyProgressRankingData = useMemo(() => empresas.map(emp => {
    const empOkrs = okrs.filter(o => o.empresa_id === emp.id);
    const avg = empOkrs.length > 0 ? Math.round(empOkrs.reduce((sum, o) => sum + o.progresso_geral, 0) / empOkrs.length) : 0;
    return { empresa: emp.nome, value: avg };
  }), [empresas, okrs]);

  const completionRateByCompanyData = useMemo(() => empresas.map(emp => {
    const empOkrs = okrs.filter(o => o.empresa_id === emp.id);
    const done = empOkrs.filter(o => o.status === 'feito').length;
    const rate = empOkrs.length > 0 ? Math.round(done / empOkrs.length * 100) : 0;
    return { empresa: emp.nome, value: rate };
  }), [empresas, okrs]);

  // Drill-down de uma empresa específica (superadmin)
  const drill = useMemo(() => {
    if (!isSuperAdmin || empresaSelecionada === 'all') return null;
    const eid = empresaSelecionada;
    const empOkrs = okrs.filter(o => o.empresa_id === eid);
    const empDeps = departamentos.filter(d => d.empresa_id === eid);
    const empUsers = usuarios.filter(u => u.empresa_id === eid);
    const empKrs = keyResults.filter(kr => empOkrs.some(o => o.id === kr.okr_id));
    const okrsPorStatus = empOkrs.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const total = empOkrs.length;
    const progressoMedio = total > 0 ? Math.round(empOkrs.reduce((sum, o) => sum + o.progresso_geral, 0) / total) : 0;
    const taxaConclusao = total > 0 ? Math.round((okrsPorStatus.feito || 0) / total * 100) : 0;
    return {
      okrsPorStatus,
      totalOkrs: total,
      totalDepartamentos: empDeps.length,
      totalUsuarios: empUsers.length,
      progressoMedio,
      taxaConclusao,
      departmentChartData: empDeps.map(dep => ({
        id: dep.id,
        nome: dep.nome,
        okrCount: empOkrs.filter(o => o.departamento_id === dep.id).length,
      })),
      progressByDepartmentData: empDeps.map(dep => {
        const d = empOkrs.filter(o => o.departamento_id === dep.id);
        return { name: dep.nome, value: d.length > 0 ? Math.round(d.reduce((sum, o) => sum + o.progresso_geral, 0) / d.length) : 0 };
      }),
      completionRateByDepartmentData: empDeps.map(dep => {
        const d = empOkrs.filter(o => o.departamento_id === dep.id);
        const c = d.filter(o => o.status === 'feito').length;
        return { name: dep.nome, value: d.length > 0 ? Math.round(c / d.length * 100) : 0 };
      }),
      krProgressData: empKrs.slice(0, 6).map(kr => ({
        name: kr.descricao.length > 20 ? kr.descricao.substring(0, 20) + '...' : kr.descricao,
        value: kr.percentual,
      })),
    };
  }, [isSuperAdmin, empresaSelecionada, okrs, departamentos, usuarios, keyResults]);

  const empresaSelecionadaNome = empresas.find(e => e.id === empresaSelecionada)?.nome;
  if (isLoading) {
    return <AppLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-muted rounded-lg" />)}
          </div>
        </div>
      </AppLayout>;
  }
  const totalOkrs = stats?.totalOkrs || 0;
  const okrsPorStatus = stats?.okrsPorStatus || {};
  return <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              {isSuperAdmin
                ? (empresaSelecionada === 'all'
                    ? 'Comparativo entre empresas'
                    : `Métricas de ${empresaSelecionadaNome ?? 'empresa'}`)
                : user?.tipo === 'admin' ? 'Visão geral da sua empresa' : 'Seus objetivos e resultados'}
              {!isSuperAdmin && empresaCnpj && ` · CNPJ: ${maskCNPJ(empresaCnpj)}`}
            </p>
          </div>
        </div>

        {/* Super Admin Stats — visão consolidada */}
        {isSuperAdmin && empresaSelecionada === 'all' && <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <StatCard title="Total de Empresas" value={stats?.totalEmpresas || 0} icon={<Building2 className="h-5 w-5" />} description={`${stats?.empresasAtivas || 0} ativas`} href="/empresas" />
            <StatCard title="Total de Usuários" value={stats?.totalUsuarios || 0} icon={<Users className="h-5 w-5" />} href="/usuarios" />
            <StatCard title="Total de OKRs" value={stats?.totalOkrs || 0} icon={<Target className="h-5 w-5" />} href="/okrs" />
            <StatCard title="Taxa de Conclusão" value={`${totalOkrs > 0 ? Math.round((okrsPorStatus.feito || 0) / totalOkrs * 100) : 0}%`} icon={<TrendingUp className="h-5 w-5" />} href="/okrs" />
            <StatCard title="Canceladas" value={okrsPorStatus.cancelado || 0} icon={<XCircle className="h-5 w-5" />} className="border-red-200" href="/okrs" />
          </div>}

        {/* Super Admin Stats — drill-down de uma empresa */}
        {isSuperAdmin && drill && <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <StatCard title="Total de OKRs" value={drill.totalOkrs} icon={<Target className="h-5 w-5" />} />
            <StatCard title="Progresso Médio" value={`${drill.progressoMedio}%`} icon={<TrendingUp className="h-5 w-5" />} />
            <StatCard title="Taxa de Conclusão" value={`${drill.taxaConclusao}%`} icon={<CheckCircle2 className="h-5 w-5" />} />
            <StatCard title="Departamentos" value={drill.totalDepartamentos} icon={<Building2 className="h-5 w-5" />} />
            <StatCard title="Usuários" value={drill.totalUsuarios} icon={<Users className="h-5 w-5" />} />
            <StatCard title="Canceladas" value={drill.okrsPorStatus.cancelado || 0} icon={<XCircle className="h-5 w-5" />} className="border-red-200" />
          </div>}

        {/* Admin Stats */}
        {user?.tipo === 'admin' && <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <StatCard title="Departamentos" value={stats?.totalDepartamentos || 0} icon={<Building2 className="h-5 w-5" />} href="/departamentos" />
            <StatCard title="Usuários" value={stats?.totalUsuarios || 0} icon={<Users className="h-5 w-5" />} href="/usuarios" />
            <StatCard title="Total de OKRs" value={stats?.totalOkrs || 0} icon={<Target className="h-5 w-5" />} href="/okrs" />
            <StatCard title="Progresso Médio" value={`${stats?.progressoMedio || 0}%`} icon={<TrendingUp className="h-5 w-5" />} href="/kanban" />
            <StatCard title="Canceladas" value={okrsPorStatus.cancelado || 0} icon={<XCircle className="h-5 w-5" />} className="border-red-200" href="/okrs" />
          </div>}

        {/* User Stats */}
        {user?.tipo === 'comum' && <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Total de OKRs" value={stats?.totalOkrs || 0} icon={<Target className="h-5 w-5" />} href="/okrs" />
            <StatCard title="Progresso Médio" value={`${stats?.progressoMedio || 0}%`} icon={<TrendingUp className="h-5 w-5" />} href="/kanban" />
            <StatCard title="Concluídas" value={okrsPorStatus.feito || 0} icon={<CheckCircle2 className="h-5 w-5" />} href="/okrs" />
            <StatCard title="Canceladas" value={okrsPorStatus.cancelado || 0} icon={<XCircle className="h-5 w-5" />} className="border-red-200" href="/okrs" />
          </div>}

        {/* Charts Section */}
        <div>
          {/* Super Admin — visão consolidada (comparativo entre empresas) */}
          {isSuperAdmin && empresaSelecionada === 'all' && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <OKRsByStatusChart okrsPorStatus={okrsPorStatus} />
              {companyProgressRankingData.length > 0 && <CompanyProgressRankingChart data={companyProgressRankingData} />}
              {completionRateByCompanyData.length > 0 && <CompletionRateByCompanyChart data={completionRateByCompanyData} />}
              {okrsByCompanyData.length > 0 && <OKRsByCompanyChart data={okrsByCompanyData} />}
              {usersByCompanyData.length > 0 && <UsersByCompanyChart data={usersByCompanyData} />}
              {usersByTypeData.length > 0 && <UsersByTypeChart users={usersByTypeData} />}
            </div>
          )}

          {/* Super Admin — drill-down de uma empresa */}
          {isSuperAdmin && drill && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <OKRsByStatusChart okrsPorStatus={drill.okrsPorStatus} />
              {drill.departmentChartData.length > 0 && <OKRsByDepartmentChart departments={drill.departmentChartData} />}
              {drill.progressByDepartmentData.length > 0 && <ProgressByDepartmentChart data={drill.progressByDepartmentData} />}
              {drill.completionRateByDepartmentData.length > 0 && <CompletionRateByDepartmentChart data={drill.completionRateByDepartmentData} />}
              {drill.krProgressData.length > 0 && <KRProgressChart data={drill.krProgressData} />}
            </div>
          )}

          {/* Admin / Usuário comum */}
          {!isSuperAdmin && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <OKRsByStatusChart okrsPorStatus={okrsPorStatus} />
              {departamentos.length > 0 && <OKRsByDepartmentChart departments={departmentChartData} />}
              {user?.tipo === 'admin' && usersByTypeData.length > 0 && <UsersByTypeChart users={usersByTypeData} />}
              {departamentos.length > 0 && <CompletionRateByDepartmentChart data={completionRateByDepartmentData} />}
              {krProgressData.length > 0 && <KRProgressChart data={krProgressData} />}
            </div>
          )}
        </div>

        {/* Progress Overview */}
        {(() => {
          const progresso = drill ? drill.progressoMedio : stats?.progressoMedio;
          if (progresso === undefined) return null;
          return <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progresso Geral</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progresso médio das OKRs</span>
                  <span className="font-medium">{progresso}%</span>
                </div>
                <Progress value={progresso} className="h-2" />
              </div>
            </CardContent>
          </Card>;
        })()}
      </div>
    </AppLayout>;
}